import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowUp, FiArrowDown, FiInfo } = FiIcons;

const MetricCard = ({ title, value, subtitle, icon, trend, trendUp, color = 'blue', explanation }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipAlign, setTooltipAlign] = useState('center');
  const cardRef = useRef(null);

  const themeColors = {
    blue: {
      gradient: 'from-blue-600 to-indigo-600 shadow-blue-200',
      light: 'bg-blue-50/50',
      accent: 'text-blue-600',
      border: 'bg-blue-500'
    },
    green: {
      gradient: 'from-emerald-500 to-teal-600 shadow-emerald-200',
      light: 'bg-emerald-50/50',
      accent: 'text-emerald-600',
      border: 'bg-emerald-500'
    },
    purple: {
      gradient: 'from-purple-600 to-violet-600 shadow-purple-200',
      light: 'bg-purple-50/50',
      accent: 'text-purple-600',
      border: 'bg-purple-500'
    },
    orange: {
      gradient: 'from-orange-500 to-amber-600 shadow-orange-200',
      light: 'bg-orange-50/50',
      accent: 'text-orange-600',
      border: 'bg-orange-500'
    },
    red: {
      gradient: 'from-rose-500 to-red-600 shadow-rose-200',
      light: 'bg-rose-50/50',
      accent: 'text-rose-600',
      border: 'bg-rose-500'
    }
  };

  const activeTheme = themeColors[color] || themeColors.blue;

  useEffect(() => {
    if (showTooltip && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      
      if (rect.left < 100) {
        setTooltipAlign('left');
      } else if (screenWidth - rect.right < 100) {
        setTooltipAlign('right');
      } else {
        setTooltipAlign('center');
      }
    }
  }, [showTooltip]);

  const alignmentClasses = {
    left: 'left-0 translate-x-0',
    center: 'left-1/2 -translate-x-1/2',
    right: 'right-0 translate-x-0'
  };

  const arrowClasses = {
    left: 'left-6',
    center: 'left-1/2 -translate-x-1/2',
    right: 'right-6'
  };

  return (
    <motion.div 
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip(!showTooltip)}
      className={`relative overflow-visible bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 group cursor-pointer ${showTooltip ? 'z-[120]' : 'z-10'} hover:z-[120]`}
    >
      {/* Refined Smart Tooltip - Now showing at the BOTTOM */}
      <AnimatePresence>
        {showTooltip && explanation && (
          <motion.div 
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            className={`absolute z-[150] top-full mt-4 w-[240px] p-3.5 bg-white rounded-2xl shadow-[0_12px_35px_rgba(15,23,42,0.12)] border border-slate-100 pointer-events-none ${alignmentClasses[tooltipAlign]}`}
          >
            {/* Subtle Accent Bar */}
            <div className={`absolute top-0 left-3 right-3 h-[2px] rounded-full ${activeTheme.border} opacity-60`} />
            
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 mb-0.5">
                <SafeIcon icon={FiInfo} className={`${activeTheme.accent} text-[9px]`} />
                <p className="text-[8px] font-black uppercase text-slate-400 tracking-[0.2em]">Strategy Note</p>
              </div>
              <p className="text-[11px] font-semibold leading-relaxed text-slate-600 tracking-tight">
                {explanation}
              </p>
            </div>
            
            {/* Tooltip Arrow - Now at the TOP */}
            <div className={`absolute -top-1.5 w-3 h-3 bg-white rotate-45 border-l border-t border-slate-100 ${arrowClasses[tooltipAlign]}`} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`absolute -right-4 -bottom-4 w-24 h-24 rounded-full ${activeTheme.light} opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl`} />
      
      <div className="flex items-start justify-between relative z-10">
        <div className="flex-1">
          <p className="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1.5">{title}</p>
          <p className="text-2xl font-black text-slate-900 tracking-tighter mb-1">{value}</p>
          {subtitle && (
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{subtitle}</p>
          )}
          {trend && (
            <div className={`flex items-center space-x-1 mt-3 px-2 py-1 rounded-lg w-fit ${trendUp ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
              <SafeIcon icon={trendUp ? FiArrowUp : FiArrowDown} className="text-[10px]" />
              <span className="text-[9px] font-black uppercase tracking-widest">{trend}</span>
            </div>
          )}
        </div>
        <div className={`w-14 h-14 bg-gradient-to-br ${activeTheme.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
          <div className="absolute inset-0 bg-white/20 rounded-2xl animate-pulse group-hover:animate-none" />
          <SafeIcon icon={icon} className="text-white text-2xl relative z-10 drop-shadow-md" />
        </div>
      </div>
    </motion.div>
  );
};

export default MetricCard;